/**
 * Putting T-Ride on a phone's home screen.
 *
 * The app has been installable all along (gatsby-plugin-manifest + gatsby-plugin-offline), and almost
 * nobody knew: a browser only hints at it, and Safari never does. Installed, it opens from its own
 * icon, full screen, straight onto the dashboard — which is how this app is used, on the way out of
 * the door or at a bus stop.
 *
 * Two browsers, two ways:
 *  - Chrome / Edge / Samsung Internet fire `beforeinstallprompt`, which can be kept and replayed from
 *    our own button. It fires once, early, often before React has mounted, so it is captured here
 *    from `onClientEntry` (gatsby-browser.js) and handed to whoever asks later.
 *  - iPhone and iPad have no such event: installing is Share → "Add to Home Screen", which the page
 *    can only explain — and where that Share button sits depends on the browser, so the wording follows
 *    the browser rather than naming Safari's bar to someone using Chrome.
 *
 * What installing does and does not give: the app's own files are kept by the service worker, so it
 * opens at once, full screen, with no address bar. Rides, points and notifications are read from the
 * API on every visit and are never cached (gatsby-config.js says why), so with no connection it opens
 * and says it could not load them.
 */

let deferred = null;
let known = false;                     // the browser told us an installed copy exists
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

/*
 * Remembered on this device, because the page is told only once that it was installed — and dated,
 * because the icon can be deleted without telling anyone. A browser that can answer the question
 * clears this as soon as it says the app is gone (below); an iPhone answers nothing, so the memory
 * runs out instead and the card comes back rather than never being offered again.
 */
const INSTALLED_KEY = "tride-installed";
const MEMORY_DAYS = 45;
const remember = () => { try { window.localStorage.setItem(INSTALLED_KEY, String(Date.now())); } catch (_) { /* this visit only */ } };
const forget = () => { try { window.localStorage.removeItem(INSTALLED_KEY); } catch (_) { /* nothing to clear */ } };
const remembered = () => {
  try {
    const at = Number(window.localStorage.getItem(INSTALLED_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < MEMORY_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
};

/** Called once, before the app renders. */
export const captureInstallPrompt = () => {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();                 // our card offers it, not the browser's mini-infobar
    deferred = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    known = true;
    remember();
    notify();
  });
  /*
   * Opened in a browser tab while an installed copy already exists on the device: nothing above fires,
   * so the card would offer an install that is already done. Chrome can be asked (it answers for this
   * app because the manifest names itself under `related_applications`); Safari cannot, which is why
   * the answer is also remembered from `appinstalled` above and from a visit that ran standalone.
   */
  if (isStandalone()) remember();
  navigator.getInstalledRelatedApps?.().then((apps) => {
    if (apps && apps.length) { known = true; remember(); notify(); return; }
    // The browser can see the device's installed apps and this is not among them: the icon was never
    // added, or it was deleted. Offer it again.
    if (!isStandalone()) { known = false; forget(); notify(); }
  }).catch(() => { /* not supported, or refused: fall back to what is remembered */ });
};

export const subscribeInstall = (fn) => {
  listeners.add(fn);
  // Called once straight away: the browser's answers (`beforeinstallprompt`, `getInstalledRelatedApps`)
  // often arrive before the card mounts, and a listener that only hears later changes would keep
  // whatever it read first — an install offer already answered, or a card hidden for an icon since
  // deleted.
  fn();
  return () => listeners.delete(fn);
};

/** Whether this browser can install with one tap right now. */
export const canPromptInstall = () => !!deferred;

/** Show the browser's own install dialog; resolves to true when the person accepted. */
export const promptInstall = async () => {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  notify();
  try {
    await e.prompt();
    const choice = await e.userChoice;
    return choice?.outcome === "accepted";
  } catch (_) {
    return false;
  }
};

/** Running from the home screen right now (Android's standalone display mode, or iOS's own flag). */
export const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return !!(window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);
};

/**
 * On the home screen already — whether or not this page is the installed copy.
 *
 * Three ways of knowing, because no single one covers a browser tab opened after installing: running
 * standalone, the browser's own `appinstalled` or `getInstalledRelatedApps`, and what either of those
 * left remembered on this device.
 */
export const isInstalled = () => isStandalone() || known || remembered();

/** Safari itself, rather than Chrome, Edge or Firefox wearing its engine on iOS. */
export const isIosSafari = () => {
  if (typeof navigator === "undefined") return false;
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/i.test(navigator.userAgent || "");
};

/** An iPhone or iPad, including an iPad that says it is a Mac. */
export const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
};
